import { useCallback, useMemo } from 'react'
import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { FetchStatuses } from 'builder/modules/constants'
import { selectors } from 'builder/modules/resumeEditor'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { Skeleton } from 'builder/Skeleton/Skeleton'
import Icon24 from 'builder/components/Icon'
import { TailoringFromPublicJobBoardStep2Buttons } from 'builder/components/DocumentEditor/TailoringFromPublicJobBoard'
import { useTailoredResumeEdit } from '../../hooks/useTailoredResumeEdit'
import { EditDrawer } from '../EditDrawer/EditDrawer'
import { AutotailoringDashboardTipBanner } from '../AutotailoringDashboardTipBanner/AutotailoringDashboardTipBanner'
import { EditContainerV2 } from '../EditContainerV2/EditContainerV2'
import {
  Container,
  Header,
  Title,
  JobTitle,
  ScoreContainer,
  TitleWrapper,
  ContainerWrapper,
  ScoreLoading,
  Spinner,
  ScoreValue,
  ScoreItem,
  FinishEditButton,
  TabletButtonsWrapper,
} from './styles'

type Props = {
  onDrawerInitialHeightChange: (height: string) => void
}

export const TailoredResumeEditForPublicJobTracker = (props: Props) => {
  const { onDrawerInitialHeightChange } = props
  const { i18n } = useI18n()
  const {
    oldScore,
    newScore,
    tailoredDesignation,
    toggleEditContainer,
    setIsEditActive,
    isEditActive,
    currentResume,
    originalResume,
  } = useTailoredResumeEdit()

  const handleFinishEditButton = useCallback(() => {
    setIsEditActive(false)
  }, [setIsEditActive])

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )

  const isResumeLoading = useTypedSelector(selectors.isResumeLoading)
  const isLoading =
    isResumeLoading ||
    autoTailoringFromPublicJobBoard.processingAutoTailoring === FetchStatuses.loading

  const { isTablet } = useMediaQueries()
  const EditSection = useMemo(() => {
    return (
      <ContainerWrapper>
        <Container isEditActive={isEditActive}>
          <Header>
            <TitleWrapper>
              <Title data-testid="autotailoring-overview-modal-title">
                {i18n.t(
                  `builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.title${
                    isLoading ? '' : '_complete'
                  }`,
                )}
                {isLoading && <Spinner />}
              </Title>

              <JobTitle>{tailoredDesignation}</JobTitle>
            </TitleWrapper>
            <ScoreContainer>
              {!isLoading ? (
                <>
                  {isTablet ? (
                    <ScoreItem>
                      <ScoreValue score={newScore}>{newScore}%</ScoreValue>
                      {i18n.t(
                        'builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.score',
                      )}
                    </ScoreItem>
                  ) : (
                    <>
                      <ScoreItem>
                        <ScoreValue score={oldScore}>{oldScore}%</ScoreValue>
                        {i18n.t(
                          'builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.previous_score',
                        )}
                      </ScoreItem>

                      <Icon24.ArrowRight />
                      <ScoreItem>
                        <ScoreValue score={newScore}>{newScore}%</ScoreValue>
                        {i18n.t(
                          'builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.current_score',
                        )}
                      </ScoreItem>
                    </>
                  )}
                </>
              ) : (
                <ScoreLoading>
                  <Skeleton width={32} height={20} />
                  {i18n.t('builder.prefill_resume.auto_tailoring_view.tailored_resume_edit.score')}
                </ScoreLoading>
              )}
            </ScoreContainer>
          </Header>

          <EditContainerV2
            isEditActive={isEditActive}
            setIsEditActive={setIsEditActive}
            resumeOld={originalResume}
            resumeTailored={currentResume}
            isLoading={isLoading}
          />

          {!isTablet && isEditActive && (
            <FinishEditButton onClick={handleFinishEditButton}>
              {i18n.t('builder.auto_tailoring.overview.finish_edit_button')}
            </FinishEditButton>
          )}
        </Container>

        {isTablet && (
          <TabletButtonsWrapper>
            <TailoringFromPublicJobBoardStep2Buttons />
          </TabletButtonsWrapper>
        )}

        {!isTablet && !isEditActive && <AutotailoringDashboardTipBanner />}
      </ContainerWrapper>
    )
  }, [
    isEditActive,
    i18n,
    isLoading,
    tailoredDesignation,
    oldScore,
    newScore,
    setIsEditActive,
    originalResume,
    currentResume,
    handleFinishEditButton,
    isTablet,
  ])

  return isTablet ? (
    <EditDrawer
      isOpen={isEditActive}
      onToggle={toggleEditContainer}
      onInitialHeightChange={onDrawerInitialHeightChange}
    >
      {EditSection}
    </EditDrawer>
  ) : (
    EditSection
  )
}
